import styled from 'styled-components';
import { Times } from '@styled-icons/fa-solid/Times';
import { Sun } from '@styled-icons/fa-solid/Sun';
import { VolumeUp } from '@styled-icons/fa-solid/VolumeUp';
import { VolumeMute } from '@styled-icons/fa-solid/VolumeMute';
import { useState, useEffect } from 'react';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { getAuth, signOut } from 'firebase/auth';
import Modal from 'react-modal';
import { toggleSounds, areSoundsEnabled } from './../../utils/soundUtils';
import { toggleDarkMode } from './../../utils/localstorage';
import { device } from './../../constants/device-sizes';
import { API_URL } from './../../constants/string-constants';
import { getSiteName } from '../../hooks/useSiteConfig';
import FeedbackModal from './FeedbackModal';
import DeleteAccountModal from './DeleteAccountModal';

const CloseButton = styled(Times)`
  cursor: pointer;
  position: absolute;
  width: 2.5rem;
  height: 2.5rem;
  top: 1rem;
  right: 1rem;
  margin: 1rem 1rem;
  font-size: 2rem;
  transition: all 0.2s linear;
  color: var(--clr-sec);
  :hover {
    color: red;
  }
`;

const SunIcon = styled(Sun)`
  cursor: pointer;
  position: absolute;
  width: 2rem;
  height: 2rem;
  top: 1rem;
  left: 1rem;
  margin: 1rem 1rem;
  font-size: 1.5rem;
  transition: all 0.2s linear;
  color: var(--clr-sec);
  :hover {
    color: #ffd700;
  }
`;

const SoundIcon = styled.div`
  cursor: pointer;
  position: absolute;
  width: 2rem;
  height: 2rem;
  top: 1rem;
  left: 5rem; /* Position next to sun icon */
  margin: 1rem 1rem;
  font-size: 1.5rem;
  transition: all 0.2s linear;
  color: var(--clr-sec);
  display: flex;
  align-items: center;
  justify-content: center;

  :hover {
    color: ${(props) => (props.$isMuted ? '#ff6b6b' : '#4ecdc4')};
  }

  svg {
    width: 2rem;
    height: 2rem;
  }
`;

const SettingsContainer = styled.div`
  padding-top: 5rem;
  padding-bottom: 2.5rem;
  padding-left: 0;
  padding-right: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  max-width: 90%;
  margin: 0 auto;
`;

const SettingsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ColumnTitle = styled.h3`
  color: var(--clr-sec);
  text-align: center;
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  opacity: 0.9;
`;

const SettingsItem = styled.div`
  padding: 0.75rem;
  background-color: var(--clr-prim);
  border-radius: 2rem;
  color: var(--clr-sec);
  border: ${(props) =>
    props.$special
      ? '2px solid #4ecdc4'
      : '1px solid color-mix(in srgb, var(--clr-sec) 30%, transparent)'};
  cursor: pointer;
  text-align: center;
  transition: all 0.2s ease;
  ${(props) =>
    props.$special &&
    `
    background: linear-gradient(135deg, var(--clr-prim) 0%, color-mix(in srgb, #4ecdc4 10%, var(--clr-prim)) 100%);
    box-shadow: 0 0 15px color-mix(in srgb, #4ecdc4 30%, transparent);
  `}

  &:hover {
    transform: translateY(-2px);
    ${(props) =>
      props.$special &&
      `
      box-shadow: 0 4px 20px color-mix(in srgb, #4ecdc4 40%, transparent);
      border-color: #5fd4cc;
    `}
  }

  a {
    text-decoration: none;
    color: inherit;
    display: block;
    margin: -0.75rem;
    padding: 0.75rem;
  }
`;

const DangerItem = styled(SettingsItem)`
  border-color: rgba(255, 107, 107, 0.4);
  color: #ff6b6b;

  &:hover {
    border-color: #ff6b6b;
    background: rgba(255, 107, 107, 0.1);
  }
`;

const ModalWrapper = styled.div`
  width: 100%;
  padding: 1rem;
  @media ${device.tablet} {
    width: 50%;
    position: auto;
    top: auto;
  }
  top: 0px;
  position: absolute;
`;

const ModalContent = styled.div`
  padding: 16px;
  background-color: var(--clr-bg);
  width: 100%;
  border-radius: 12px;
  shadow-opacity: 0.18;
  shadow-radius: 4px;
  shadow-color: #000;
  shadow-offset: 0px 4px;
  elevation: 10;
  a {
    text-decoration: none;
    color: inherit;
    display: block;
    margin: -1rem;
    padding: 1rem;
  }
`;

const SettingsMenu = (props) => {
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [userStatus, setUserStatus] = useState({
    hasSubscription: false,
    hasPaidSubscription: false,
    hasAccountNumber: false,
    hasAuthentication: false,
  });
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const [deleteAccountModalVisible, setDeleteAccountModalVisible] = useState(false);
  const [soundsEnabled, setSoundsEnabled] = useState(true);

  useEffect(() => {
    if (props.visible) {
      checkUserStatus();
      // Update sound state when settings menu opens
      setSoundsEnabled(areSoundsEnabled());
    }
  }, [props.visible]);

  const checkUserStatus = async () => {
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + 'users/status', {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      if (response.ok) {
        const status = await response.json();
        setUserStatus({
          ...status,
          // Older/no-user status responses omit hasAuthentication; fall back to
          // the account number so logout still shows for legacy accounts.
          hasAuthentication: status.hasAuthentication ?? !!status.hasAccountNumber,
        });
      }
    } catch (error) {
      console.error('Error checking user status:', error);
    }
  };

  function menuToggleDarkMode() {
    toggleDarkMode();
  }

  function menuToggleSound() {
    const newSoundState = !soundsEnabled;
    toggleSounds(newSoundState);
    setSoundsEnabled(newSoundState);

    const analytics = getAnalytics();
    logEvent(analytics, 'sound_toggled', {
      enabled: newSoundState,
    });
  }

  const handleSubscription = async () => {
    try {
      setSubscriptionLoading(true);
      const auth = getAuth();

      if (!auth.currentUser) {
        alert('Please sign in to subscribe');
        return;
      }

      const token = await auth.currentUser.getIdToken();

      // Create a form and submit it to avoid fetch redirect issues
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = API_URL + 'create-checkout-session';

      // Add lookup key
      const lookupKeyInput = document.createElement('input');
      lookupKeyInput.type = 'hidden';
      lookupKeyInput.name = 'lookup_key';
      lookupKeyInput.value = 'VentScape-Monthly';
      form.appendChild(lookupKeyInput);

      // Add auth token
      const tokenInput = document.createElement('input');
      tokenInput.type = 'hidden';
      tokenInput.name = 'auth_token';
      tokenInput.value = token;
      form.appendChild(tokenInput);

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error('Subscription error:', error);
      alert('Failed to start subscription. Please try again.');
      setSubscriptionLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      const analytics = getAnalytics();
      logEvent(analytics, 'sign_out_clicked');

      const auth = getAuth();
      await signOut(auth);

      // Clear session storage
      sessionStorage.removeItem('captcha_session_token');

      // Reload the page to reset state
      window.location.reload();
    } catch (error) {
      console.error('Sign out error:', error);
      alert('Failed to sign out. Please try again.');
    }
  };

  return (
    <Modal
      isOpen={props.visible}
      onRequestClose={props.closeSettingsMenu}
      style={{
        overlay: { zIndex: 1000, backgroundColor: 'rgba(0,0,0,0.5)' },
        content: {
          background: 'none',
          border: 'none',
          padding: 0,
          inset: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        },
      }}
      ariaHideApp={false}
    >
      <ModalWrapper>
        <ModalContent>
          <CloseButton onClick={props.closeSettingsMenu} />
          <SunIcon onClick={menuToggleDarkMode} />
          <SoundIcon
            onClick={menuToggleSound}
            $isMuted={!soundsEnabled}
            title={soundsEnabled ? 'Mute sounds' : 'Enable sounds'}
          >
            {soundsEnabled ? <VolumeUp /> : <VolumeMute />}
          </SoundIcon>
          <SettingsContainer>
            <SettingsColumn>
              <ColumnTitle>My Settings</ColumnTitle>
              <SettingsItem
                onClick={() => {
                  const analytics = getAnalytics();
                  logEvent(analytics, 'edit_profile_clicked');
                  props.openEditProfile();
                }}
              >
                Edit Profile
              </SettingsItem>
              <SettingsItem
                onClick={() => {
                  const analytics = getAnalytics();
                  logEvent(analytics, 'color_font_clicked');
                  if (props.openColorFont) {
                    props.openColorFont();
                  }
                }}
              >
                Set Color & Font
              </SettingsItem>
              <SettingsItem
                onClick={() => {
                  const analytics = getAnalytics();
                  logEvent(analytics, 'layout_toggle_clicked', {
                    current_layout: props.useChatLayout ? 'chat' : 'classic',
                  });
                  if (props.onToggleChatLayout) {
                    props.onToggleChatLayout();
                  }
                }}
              >
                {props.useChatLayout
                  ? 'Switch to Classic View'
                  : 'Switch to Chat View'}
              </SettingsItem>
              <SettingsItem
                onClick={() => {
                  const analytics = getAnalytics();
                  logEvent(analytics, 'block_list_clicked');
                  if (props.openBlockList) {
                    props.openBlockList();
                  }
                }}
              >
                Manage Blocked Users
              </SettingsItem>
              {!userStatus.hasAuthentication && (
                <SettingsItem
                  onClick={() => {
                    const analytics = getAnalytics();
                    logEvent(analytics, 'signin_or_up_clicked');
                    if (props.openSignInOrUp) {
                      props.openSignInOrUp();
                    }
                  }}
                >
                  Sign In / Create Account
                </SettingsItem>
              )}
              {/* Billing portal is only meaningful for actual Stripe
                  subscribers — global mods get Plus comped and have no
                  customer record to manage. */}
              {userStatus.hasPaidSubscription && (
                <SettingsItem>
                  <a
                    href='https://billing.stripe.com/p/login/eVqfZg5TiaEYaWZ8uQ4ow00'
                    target='_blank'
                    rel='noreferrer noopener'
                    onClick={() => {
                      const analytics = getAnalytics();
                      logEvent(analytics, 'manage_subscription_clicked');
                    }}
                  >
                    Manage Subscription
                  </a>
                </SettingsItem>
              )}
              {userStatus.hasAuthentication && (
                <SettingsItem onClick={handleSignOut}>Log Out</SettingsItem>
              )}
              {userStatus.hasAccountNumber && (
                <DangerItem
                  onClick={() => {
                    const analytics = getAnalytics();
                    logEvent(analytics, 'delete_account_clicked');
                    setDeleteAccountModalVisible(true);
                  }}
                >
                  Delete Account
                </DangerItem>
              )}
            </SettingsColumn>

            <SettingsColumn>
              <ColumnTitle>Other Stuff</ColumnTitle>
              <SettingsItem
                onClick={() => {
                  const analytics = getAnalytics();
                  logEvent(analytics, 'feedback_button_clicked');
                  setFeedbackModalVisible(true);
                }}
              >
                Feedback / Bug Report
              </SettingsItem>
              <SettingsItem
                onClick={() => {
                  const analytics = getAnalytics();
                  logEvent(analytics, 'instructions_clicked');
                  props.openInstructions();
                }}
              >
                Instructions
              </SettingsItem>
              <SettingsItem>
                <a
                  href='https://www.reddit.com/r/VentScape/'
                  target='_blank'
                  rel='noreferrer noopener'
                >
                  Join the {getSiteName()} Subreddit
                </a>
              </SettingsItem>
              <SettingsItem
                onClick={() => {
                  const analytics = getAnalytics();
                  logEvent(analytics, 'discord_clicked', {
                    referrer: 'settings',
                  });
                }}
              >
                <a
                  href='https://discord.gg/rM7g5Zusmr'
                  target='_blank'
                  rel='noreferrer noopener'
                >
                  Join the {getSiteName()} Discord
                </a>
              </SettingsItem>
              <SettingsItem
                $special={false}
                onClick={() => {
                  const analytics = getAnalytics();
                  logEvent(analytics, 'faq_clicked', {
                    referrer: 'settings',
                  });
                }}
              >
                <a
                  href='/faq'
                  rel='noreferrer noopener'
                >
                  FAQ
                </a>
              </SettingsItem>
              <SettingsItem
                onClick={() => {
                  const analytics = getAnalytics();
                  logEvent(analytics, 'donate_clicked', {
                    referrer: 'settings',
                  });
                }}
              >
                <a
                  href='https://buymeacoffee.com/ventscape'
                  target='_blank'
                  rel='noreferrer noopener'
                >
                  Donate
                </a>
              </SettingsItem>
            </SettingsColumn>
          </SettingsContainer>
        </ModalContent>
      </ModalWrapper>
      <FeedbackModal
        visible={feedbackModalVisible}
        onClose={() => setFeedbackModalVisible(false)}
      />
      <DeleteAccountModal
        visible={deleteAccountModalVisible}
        onClose={() => setDeleteAccountModalVisible(false)}
      />
    </Modal>
  );
};

export default SettingsMenu;
